import React, { useEffect, useRef } from 'react';

// Matchplay is one format here — singles vs pairs is a follow-up choice
// shown only when it's selected (the stored format stays 'Singles Matchplay'
// or 'Pairs Matchplay').
const FORMATS = [
    { value: 'Stableford', label: 'Stableford' },
    { value: 'Stroke Play', label: 'Stroke Play' },
    { value: 'matchplay', label: 'Matchplay' },
];

const ALLOWANCES = [
    { value: 100, label: '100%' },
    { value: 95, label: '95%' },
    { value: 90, label: '90%' },
    { value: 85, label: '85%' },
];

/**
 * `quick` is the quick round's version: a second card under "Where are you
 * playing?" rather than a step of its own. Everything a casual round changes is
 * ON SCREEN with its default already chosen — it used to be one collapsed
 * "Stableford · 95% handicap · Change" strip that opened the full step below
 * the fold, so the default was easy to miss and changing it meant scrolling
 * blind. The one setting nobody changes (course adjustment) is a single switch
 * line, which is what lets the card fit an iPhone 15 under the course card.
 */
const Step3_Scoring = ({ data, updateData, isLocked, showSignupType, quick = false }) => {
    const matchTypeRef = useRef(null);
    const isStarted = isLocked || data.courses?.some(c => c.is_started);
    const isMatchplay = ['Singles Matchplay', 'Pairs Matchplay'].includes(data.format);
    const displayFormat = isMatchplay ? 'matchplay' : data.format;

    // Picking Matchplay grows the card by a field. Bring it into view, or the
    // answer to "singles or pairs?" appears below the fold with nothing to say so.
    const prevMatchplay = useRef(isMatchplay);
    useEffect(() => {
        if (isMatchplay && !prevMatchplay.current && matchTypeRef.current?.scrollIntoView) {
            matchTypeRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
        prevMatchplay.current = isMatchplay;
    }, [isMatchplay]);

    const handleFormatChange = (format) => {
        if (isStarted) return;
        if (format === 'matchplay') {
            // Keep an existing matchplay type; default new picks to singles.
            if (!isMatchplay) updateData({ format: 'Singles Matchplay', matchplay_percentage: 100 });
            return;
        }
        updateData({ format, matchplay_percentage: 95 });
    };

    const handleMatchTypeChange = (format) => {
        if (isStarted || data.format === format) return;
        updateData({ format, matchplay_percentage: format === 'Singles Matchplay' ? 100 : 90 });
    };

    return (
        <div className={`wiz-step${quick ? ' wiz-step--scoring' : ''}`}>
            <h2 className="wiz-header">{quick ? 'How are you scoring?' : 'How should we score it?'}</h2>
            {!quick && <p className="wiz-subtitle">Pick the format and handicap rules for this round.</p>}

            {isStarted && (
                <div className="wiz-locked-banner">
                    Scoring has started — these settings are locked
                </div>
            )}

            <div className="wiz-field">
                <label className="wiz-label">Format</label>
                <div className="wiz-pills wiz-pills--row">
                    {FORMATS.map(f => (
                        <button
                            key={f.value}
                            type="button"
                            className={`wiz-pill ${displayFormat === f.value ? 'wiz-pill--active' : ''}`}
                            onClick={() => handleFormatChange(f.value)}
                            disabled={isStarted}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>

            {isMatchplay && (
                <div className="wiz-field" ref={matchTypeRef}>
                    <label className="wiz-label">Match Type</label>
                    <div className="wiz-toggle">
                        <button
                            type="button"
                            className={data.format === 'Singles Matchplay' ? 'active' : ''}
                            onClick={() => handleMatchTypeChange('Singles Matchplay')}
                            disabled={isStarted}
                        >Singles</button>
                        <button
                            type="button"
                            className={data.format === 'Pairs Matchplay' ? 'active' : ''}
                            onClick={() => handleMatchTypeChange('Pairs Matchplay')}
                            disabled={isStarted}
                        >Pairs</button>
                    </div>
                    <span className="wiz-hint">
                        {data.format === 'Singles Matchplay'
                            ? 'One against one — matches are groups of 2'
                            : 'Fourball better ball — two pairs per match'}
                    </span>
                </div>
            )}

            <div className="wiz-field">
                <label className="wiz-label">
                    {isMatchplay ? 'Handicap Difference' : 'Handicap Allowance'}
                </label>
                <div className="wiz-pills wiz-pills--row">
                    {ALLOWANCES.map(a => (
                        <button
                            key={a.value}
                            type="button"
                            className={`wiz-pill ${String(data.matchplay_percentage) === String(a.value) ? 'wiz-pill--active' : ''}`}
                            onClick={() => !isStarted && updateData({ matchplay_percentage: a.value })}
                            disabled={isStarted}
                        >
                            {a.label}
                        </button>
                    ))}
                </div>
                {/* The quick card drops the stroke-play hint: "Handicap
                    allowance" over 95% says it, and the line is what kept the
                    card off one screen. Matchplay's rule is genuinely different. */}
                {(!quick || isMatchplay) && <span className="wiz-hint">
                    {isMatchplay
                        ? '% of handicap difference — lowest handicapper in each match receives 0 shots'
                        : '% of course handicap applied to each player'}
                </span>}
            </div>

            {quick ? (
                <label className="wiz-switch-row">
                    <span className="wiz-switch-row__text">
                        <span className="wiz-switch-row__title">Adjust for course difficulty</span>
                        <span className="wiz-switch-row__hint">
                            {data.use_simple_handicap ? 'Off — Handicap Index only' : 'WHS slope and course rating'}
                        </span>
                    </span>
                    <input
                        type="checkbox"
                        role="switch"
                        className="wiz-switch"
                        checked={!data.use_simple_handicap}
                        onChange={(e) => !isStarted && updateData({ use_simple_handicap: !e.target.checked })}
                        disabled={isStarted}
                    />
                </label>
            ) : (
            <div className="wiz-field">
                <label className="wiz-label">Handicap Calculation</label>
                <div className="wiz-toggle">
                    <button
                        type="button"
                        className={!data.use_simple_handicap ? 'active' : ''}
                        onClick={() => !isStarted && updateData({ use_simple_handicap: false })}
                        disabled={isStarted}
                    >Course Adjusted</button>
                    <button
                        type="button"
                        className={data.use_simple_handicap ? 'active' : ''}
                        onClick={() => !isStarted && updateData({ use_simple_handicap: true })}
                        disabled={isStarted}
                    >Standard Handicap</button>
                </div>
                <span className="wiz-hint">
                    {data.use_simple_handicap
                        ? 'Uses rounded Handicap Index only — no slope/course adjustment'
                        : 'Adjusts for course difficulty using WHS slope and course rating'}
                </span>
            </div>
            )}

            {showSignupType && (
                <div className="wiz-field">
                    <label className="wiz-label">Signup Type</label>
                    <div className="wiz-toggle">
                        <button
                            type="button"
                            className={data.signup_mode !== 'roll_up' ? 'active' : ''}
                            onClick={() => !isStarted && updateData({ signup_mode: 'standard' })}
                            disabled={isStarted}
                        >Assign Groups</button>
                        <button
                            type="button"
                            className={data.signup_mode === 'roll_up' ? 'active' : ''}
                            onClick={() => !isStarted && updateData({ signup_mode: 'roll_up' })}
                            disabled={isStarted}
                        >Tee Sheet Sign Up</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Step3_Scoring;
